/* eslint-disable barrel-files/avoid-barrel-files, barrel-files/avoid-re-export-all -- FIXME: Fix this ESLint violation!, UXP-3757 - Remove barrels from web codebase */
import createDatabase from '@openphone/dexie-database'
import type Dexie from 'dexie'

import log from '@src/lib/log'

import { AddonRepository } from './AddonRepository'
import {
  AiTriggerDefinitionRepository,
  AI_TRIGGER_DEFINITION_TABLE_NAME,
} from './AiTriggerDefinitionRepository'
import {
  CALL_FALLBACK_CONFIG_TABLE_NAME,
  CallFallbackConfigRepository,
} from './CallFallbackConfigRepository'
import { CapabilityRepository } from './CapabilityRepository'
import { CnamProfileRepository } from './CnamProfileRepository'
import {
  CONTACT_SUGGESTION_TABLE_NAME,
  ContactSuggestionRepository,
} from './ContactSuggestionRepository'
import CsvImportV2Repository from './CsvImportV2Repository'
import { GroupsRepository } from './GroupsRepository'
import { PortRequestRepository } from './PortRequestRepository'
import { RING_ORDER_TABLE_NAME, RingOrderRepository } from './RingOrderRepository'
import {
  SCHEDULED_MESSAGE_TABLE_NAME,
  ScheduledMessageRepository,
} from './ScheduledMessageRepository'
import SearchRepository from './SearchRepository'
import { SharedContactSettingsRepository } from './SharedContactSettingsRepository'
import { TagRepository, TAG_TABLE_NAME } from './TagRepository'
import TollFreeRegistrationLiteRepository, {
  TOLL_FREE_REGISTRATION_LITE_TABLE_NAME,
} from './TollFreeRegistrationLiteRepository'
import TollFreeRegistrationRepository, {
  TOLL_FREE_REGISTRATION_TABLE_NAME,
} from './TollFreeRegistrationRepository'
import { UsageRepository } from './UsageRepository'
import { WebhooksRepository } from './WebhooksRepository'
import {
  WORKFLOW_DEFINITION_TABLE_NAME,
  WorkflowDefinitionRepository,
} from './WorkflowDefinitionRepository'
import {
  WORKFLOW_STEP_DEFINITION_TABLE_NAME,
  WorkflowStepDefinitionRepository,
} from './WorkflowStepDefinitionRepository'
import {
  WORKFLOW_TRIGGER_DEFINITION_TABLE_NAME,
  WorkflowTriggerDefinitionRepository,
} from './WorkflowTriggerDefinitionRepository'
import { ActivityRepository } from './activity'
import { AlertRepository } from './alert'
import { BlocklistRepository } from './blocklist'
import { ContactRepository } from './contact'
import { ContactGoogleSettingsRepository } from './contact-settings'
import { ContactTemplateItemRepository } from './contact-template-item'
import { ConversationRepository } from './conversation'
import { DirectNumberRepository } from './direct-number'
import { EntityPhoneNumberRepository } from './entity-phone-number'
import { IntegrationRepository } from './integration'
import { IntegrationContactRepository } from './integration-contact'
import { KeyValueRepository } from './kv'
import { MemberRepository } from './member'
import { PhoneNumberRepository } from './phone-number'
import { PresenceRepository } from './presence'
import { SnippetRepository } from './snippet'
import { TransactionRepository } from './transaction'

export default class Repository {
  private database: Dexie

  activity: ActivityRepository
  addon: AddonRepository
  alert: AlertRepository
  blocklist: BlocklistRepository
  contact: ContactRepository
  contactGoogleSettings: ContactGoogleSettingsRepository
  contactSettings: SharedContactSettingsRepository
  contactTemplateItem: ContactTemplateItemRepository
  conversation: ConversationRepository
  directNumber: DirectNumberRepository
  entityPhoneNumber: EntityPhoneNumberRepository
  integration: IntegrationRepository
  integrationContact: IntegrationContactRepository
  keyValue: KeyValueRepository
  member: MemberRepository
  phoneNumber: PhoneNumberRepository
  presence: PresenceRepository
  search: SearchRepository
  snippet: SnippetRepository
  transaction: TransactionRepository
  groups: GroupsRepository
  usage: UsageRepository
  webhooks: WebhooksRepository
  scheduledMessage: ScheduledMessageRepository
  cnam: CnamProfileRepository
  portRequest: PortRequestRepository
  capability: CapabilityRepository
  ringOrder: RingOrderRepository
  workflowDefinition: WorkflowDefinitionRepository
  workflowStepDefinition: WorkflowStepDefinitionRepository
  workflowTriggerDefinition: WorkflowTriggerDefinitionRepository
  callFallbackConfig: CallFallbackConfigRepository
  contactSuggestion: ContactSuggestionRepository
  csvImports: CsvImportV2Repository
  tollFreeRegistration: TollFreeRegistrationRepository
  tollFreeRegistrationLite: TollFreeRegistrationLiteRepository
  tag: TagRepository
  aiTriggerDefinition: AiTriggerDefinitionRepository

  constructor() {
    this.database = createDatabase()

    // @ts-expect-error - Dexie doesn't work with our legacy models, but we pass them to Repository classes to make the PersistedCollection work.
    // Repositories should instead receive raw data types and return raw data types, and let the PersistedCollection handle the transformation to our legacy models, which is what the PersistedCollection is designed to do.
    // Since refactoring all of this is a huge task, we're just expecting the types to be wrong here.
    this.activity = new ActivityRepository(this.database.table('activity'))
    this.alert = new AlertRepository(this.database.table('alert'))
    this.blocklist = new BlocklistRepository(this.database.table('blocklist'))
    // @ts-expect-error - Dexie doesn't work with our legacy models, but we pass them to Repository classes to make the PersistedCollection work.
    // Repositories should instead receive raw data types and return raw data types, and let the PersistedCollection handle the transformation to our legacy models, which is what the PersistedCollection is designed to do.
    // Since refactoring all of this is a huge task, we're just expecting the types to be wrong here.
    this.contact = new ContactRepository(this.database.table('contact'))
    this.contactGoogleSettings = new ContactGoogleSettingsRepository(
      this.database.table('contactGoogleSettings'),
    )

    this.contactSettings = new SharedContactSettingsRepository(
      this.database.table('contactSettings'),
    )

    this.contactTemplateItem = new ContactTemplateItemRepository(
      this.database.table('contactTemplateItem'),
    )
    this.conversation = new ConversationRepository(this.database.table('conversation'))
    this.directNumber = new DirectNumberRepository(this.database.table('directNumber'))
    this.entityPhoneNumber = new EntityPhoneNumberRepository(
      // @ts-expect-error - Dexie doesn't work with our legacy models, but we pass them to Repository classes to make the PersistedCollection work.
      // Repositories should instead receive raw data types and return raw data types, and let the PersistedCollection handle the transformation to our legacy models, which is what the PersistedCollection is designed to do.
      // Since refactoring all of this is a huge task, we're just expecting the types to be wrong here.
      this.database.table('entityPhoneNumber'),
    )
    this.integrationContact = new IntegrationContactRepository(
      this.database.table('integrationContact'),
    )
    this.integration = new IntegrationRepository(this.database.table('integration'))
    this.keyValue = new KeyValueRepository(this.database.table('kv'))
    this.member = new MemberRepository(this.database.table('member'))
    this.phoneNumber = new PhoneNumberRepository(this.database.table('phoneNumber'))
    this.presence = new PresenceRepository(this.database.table('presence'))
    this.search = new SearchRepository(this.database.table('search'))
    this.snippet = new SnippetRepository(this.database.table('snippet'))
    this.transaction = new TransactionRepository(this.database.table('transaction'))
    this.groups = new GroupsRepository(this.database.table('groups'))
    this.webhooks = new WebhooksRepository(this.database.table('webhooks'))
    this.scheduledMessage = new ScheduledMessageRepository(
      this.database.table(SCHEDULED_MESSAGE_TABLE_NAME),
    )
    this.addon = new AddonRepository(this.database.table('addon'))
    this.usage = new UsageRepository(this.database.table('usage'))
    this.cnam = new CnamProfileRepository(this.database.table('cnam'))
    this.portRequest = new PortRequestRepository(this.database.table('portRequest'))
    this.capability = new CapabilityRepository(this.database.table('capability'))
    this.ringOrder = new RingOrderRepository(this.database.table(RING_ORDER_TABLE_NAME))
    this.workflowDefinition = new WorkflowDefinitionRepository(
      this.database.table(WORKFLOW_DEFINITION_TABLE_NAME),
    )
    this.workflowStepDefinition = new WorkflowStepDefinitionRepository(
      this.database.table(WORKFLOW_STEP_DEFINITION_TABLE_NAME),
    )
    this.workflowTriggerDefinition = new WorkflowTriggerDefinitionRepository(
      this.database.table(WORKFLOW_TRIGGER_DEFINITION_TABLE_NAME),
    )
    this.callFallbackConfig = new CallFallbackConfigRepository(
      this.database.table(CALL_FALLBACK_CONFIG_TABLE_NAME),
    )
    this.contactSuggestion = new ContactSuggestionRepository(
      this.database.table(CONTACT_SUGGESTION_TABLE_NAME),
    )
    this.csvImports = new CsvImportV2Repository(this.database.table('csvImports'))
    this.tollFreeRegistration = new TollFreeRegistrationRepository(
      this.database.table(TOLL_FREE_REGISTRATION_TABLE_NAME),
    )
    this.tollFreeRegistrationLite = new TollFreeRegistrationLiteRepository(
      this.database.table(TOLL_FREE_REGISTRATION_LITE_TABLE_NAME),
    )
    this.tag = new TagRepository(this.database.table(TAG_TABLE_NAME))
    this.aiTriggerDefinition = new AiTriggerDefinitionRepository(
      this.database.table(AI_TRIGGER_DEFINITION_TABLE_NAME),
    )
  }

  async invoke(collection: string, method: string, query: any): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-function-type -- FIXME: Fix this ESLint violation!, FIXME: Fix this ESLint violation!
    const handler: Function = this[collection]?.[method]

    if (typeof handler !== 'function') {
      throw new Error(`handler '${method}' not found for collection ${collection}`)
    }
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- FIXME: Fix this ESLint violation!
      const r = await handler.apply(this[collection], query)

      return r
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- FIXME: Fix this ESLint violation!
      log.error('worker error', { collection, method, query })
      throw error
    }
  }
}

export * from './ScheduledMessageRepository'
export * from './SearchRepository'
export * from './activity'
export * from './alert'
export * from './blocklist'
export * from './contact'
export * from './contact-settings'
export * from './contact-template-item'
export * from './conversation'
export * from './direct-number'
export * from './entity-phone-number'
export * from './integration'
export * from './integration-contact'
export * from './kv'
export * from './member'
export * from './phone-number'
export * from './presence'
export * from './snippet'
export * from './transaction'
